import { z } from "zod";

/**
 * Responsive gift arrays (CT-RM-13). A giving block in "personalized" mode
 * shows a known donor amounts derived from their own history — last gift,
 * largest gift or the scoring module's suggested ask — times a set of
 * multipliers, rounded to friendly amounts and clamped to a floor/ceiling.
 * Anonymous visitors (and donors with no history) see the block's presets.
 *
 * The API computes the array; the browser only ever receives the result.
 */

export const zGiftArrayBasis = z.enum(["last", "largest", "suggested"]);
export type GiftArrayBasis = z.infer<typeof zGiftArrayBasis>;

export const zGiftArrayMode = z.enum(["fixed", "personalized"]);
export type GiftArrayMode = z.infer<typeof zGiftArrayMode>;

export const MAX_GIFT_ARRAY = 6;

export const zGiftArrayPersonalization = z
  .object({
    basis: zGiftArrayBasis.default("last"),
    multipliers: z.array(z.number().min(0.1).max(20)).min(1).max(MAX_GIFT_ARRAY).default([1, 1.5, 2]),
    /** minor units */
    floor: z.number().int().min(100).max(100_000_000).default(10_00),
    /** minor units */
    ceiling: z.number().int().min(100).max(100_000_000).default(5_000_00),
  })
  .refine((p) => p.floor <= p.ceiling, { message: "The floor can't be above the ceiling", path: ["floor"] });
export type GiftArrayPersonalization = z.infer<typeof zGiftArrayPersonalization>;

export const DEFAULT_PERSONALIZATION: GiftArrayPersonalization = { basis: "last", multipliers: [1, 1.5, 2], floor: 10_00, ceiling: 5_000_00 };

/** the fields of a donor's record a personalized array may read (never sent to the browser) */
export interface GiftHistory {
  lastGift: number | null;
  largestGift: number | null;
  suggestedAsk: number | null;
}

/** rounding step for a friendly amount near `cents` */
export function friendlyStep(cents: number): number {
  if (cents < 50_00) return 5_00;
  if (cents < 100_00) return 10_00;
  if (cents < 500_00) return 25_00;
  if (cents < 1_000_00) return 50_00;
  if (cents < 5_000_00) return 250_00;
  if (cents < 10_000_00) return 500_00;
  return 1_000_00;
}

/** nearest friendly amount ($5s under $50, $10s under $100, $25s under $500, …); never below one step */
export function roundFriendly(cents: number): number {
  const step = friendlyStep(cents);
  return Math.max(step, Math.round(cents / step) * step);
}

/** the smallest friendly amount strictly above `cents` */
function nextFriendlyAbove(cents: number): number {
  const step = friendlyStep(cents + 1);
  return Math.floor(cents / step) * step + step;
}

/** the donor's base amount: the chosen basis, falling back to the others when it's missing */
export function giftArrayBase(history: GiftHistory, basis: GiftArrayBasis): number | null {
  const pick: Record<GiftArrayBasis, number | null> = {
    last: history.lastGift,
    largest: history.largestGift,
    suggested: history.suggestedAsk,
  };
  for (const key of [basis, "last", "largest", "suggested"] as const) {
    const v = pick[key];
    if (v != null && Number.isFinite(v) && v > 0) return v;
  }
  return null;
}

/**
 * Base × each multiplier (ascending), rounded friendly, clamped to
 * [floor, ceiling], strictly increasing (collisions step up to the next
 * friendly amount; anything over the ceiling is dropped). Null when the
 * donor has no usable history — show the default array.
 */
export function personalizedGiftArray(history: GiftHistory, config: Partial<GiftArrayPersonalization> = {}): number[] | null {
  const cfg = { ...DEFAULT_PERSONALIZATION, ...config };
  const base = giftArrayBase(history, cfg.basis);
  if (base == null) return null;
  const floor = Math.min(cfg.floor, cfg.ceiling);
  const ceiling = cfg.ceiling;
  const out: number[] = [];
  for (const m of [...cfg.multipliers].sort((a, b) => a - b).slice(0, MAX_GIFT_ARRAY)) {
    let v = Math.min(ceiling, Math.max(floor, roundFriendly(base * m)));
    const prev = out[out.length - 1];
    if (prev != null && v <= prev) v = nextFriendlyAbove(prev);
    if (v > ceiling) continue;
    out.push(v);
  }
  // the first amount is always clamped into range, so the array is never empty
  return out;
}
